import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Wallet, Loader2, Check, X, ExternalLink, Clock, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminOnly } from "@/components/admin/AdminOnly";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { rejectManualPayment, approveManualPayment } from "@/lib/manual-payments.functions";
import baridimobLogo from "@/assets/baridimob.png.asset.json";

export const Route = createFileRoute("/_authenticated/admin/payments")({
  head: () => ({ meta: [{ title: "Manual payments — Admin" }, { name: "robots", content: "noindex" }] }),
  component: () => (
    <AdminOnly>
      <AdminNav />
      <PaymentsPage />
    </AdminOnly>
  ),
});

type Row = {
  id: string;
  user_id: string;
  plan: string;
  amount_cents: number;
  currency: string;
  method: string;
  status: string;
  proof_path: string;
  sender_name: string | null;
  sender_note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  admin_notes: string | null;
  created_at: string;
  user_email: string | null;
  user_name: string | null;
};

function PaymentsPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<"pending_review" | "approved" | "rejected">("pending_review");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-manual-payments"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_get_manual_payments");
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const rows = (data ?? []).filter((r) => r.status === status);

  const approveFn = useServerFn(approveManualPayment);
  const approve = useMutation({
    mutationFn: async (id: string) => {
      await approveFn({ data: { id } });
    },
    onSuccess: () => {
      toast.success("Payment approved — plan activated and thank-you email sent");
      qc.invalidateQueries({ queryKey: ["admin-manual-payments"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rejectFn = useServerFn(rejectManualPayment);
  const reject = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      await rejectFn({ data: { id, reason } });
    },
    onSuccess: () => {
      toast.success("Payment rejected — freelancer notified by email");
      qc.invalidateQueries({ queryKey: ["admin-manual-payments"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-5">
      <AdminPageHeader
        icon={Wallet}
        title="Manual payments"
        subtitle="BaridiMob transfers awaiting verification"
        tone="text-yellow-400"
        glow="hsl(48 100% 55% / 0.30)"
      />

      <div className="flex flex-wrap items-center gap-2">
        {(["pending_review", "approved", "rejected"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              status === s
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {s === "pending_review" ? "Pending" : s[0].toUpperCase() + s.slice(1)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center rounded-2xl border bg-card py-16 text-sm text-muted-foreground">
          <Loader2 className="me-2 h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border bg-card py-16 text-center text-sm text-muted-foreground">
          No {status === "pending_review" ? "pending" : status} payments.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {rows.map((r) => (
            <PaymentCard
              key={r.id}
              row={r}
              onApprove={() => approve.mutate(r.id)}
              onReject={(reason) => reject.mutate({ id: r.id, reason })}
              busy={approve.isPending || reject.isPending}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PaymentCard({
  row,
  onApprove,
  onReject,
  busy,
}: {
  row: Row;
  onApprove: () => void;
  onReject: (reason: string) => void;
  busy: boolean;
}) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  async function loadProof() {
    setLoading(true);
    const bucket = row.proof_path.toLowerCase().endsWith(".pdf") ? "payment-proofs-pdf" : "payment-proofs";
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(row.proof_path, 300);
    setLoading(false);
    if (error) return toast.error(error.message);
    setSignedUrl(data.signedUrl);
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border bg-card p-5">
      <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-yellow-400/20 blur-3xl" aria-hidden />
      <div className="relative flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <img src={baridimobLogo.url} alt="" className="h-4 w-4 rounded-sm bg-white p-[1px]" />
            <span className="uppercase tracking-wider">{row.method}</span>
            <span>·</span>
            <span>{row.plan}</span>
          </div>
          <div className="mt-1 text-lg font-semibold">
            ${(row.amount_cents / 100).toFixed(2)}{" "}
            <span className="text-xs font-normal text-muted-foreground">{row.currency}</span>
          </div>
          <div className="mt-1 text-sm">
            <div className="font-medium">{row.user_name ?? row.sender_name ?? "—"}</div>
            <div className="text-xs text-muted-foreground">{row.user_email ?? "—"}</div>
          </div>
        </div>
        <div className="text-end text-[11px] text-muted-foreground">
          <Clock className="mb-0.5 inline h-3 w-3" /> {new Date(row.created_at).toLocaleString()}
        </div>
      </div>

      {row.sender_name && row.sender_name !== row.user_name && (
        <div className="relative mt-3 rounded-lg border bg-background/50 px-3 py-2 text-xs">
          <span className="text-muted-foreground">Sender on BaridiMob:</span>{" "}
          <span className="font-medium">{row.sender_name}</span>
        </div>
      )}
      {row.sender_note && (
        <div className="relative mt-2 rounded-lg border bg-background/50 px-3 py-2 text-xs text-muted-foreground">
          "{row.sender_note}"
        </div>
      )}

      <div className="relative mt-3">
        {!signedUrl ? (
          <Button size="sm" variant="outline" onClick={loadProof} disabled={loading} className="w-full">
            {loading ? <Loader2 className="me-2 h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="me-2 h-3.5 w-3.5" />}
            View transfer proof
          </Button>
        ) : (
          <div className="overflow-hidden rounded-lg border bg-background/60">
            {row.proof_path.toLowerCase().endsWith(".pdf") ? (
              <object data={signedUrl} type="application/pdf" className="h-72 w-full">
                <div className="p-4 text-center text-xs text-muted-foreground">PDF receipt</div>
              </object>
            ) : (
              <img src={signedUrl} alt="proof" className="max-h-72 w-full object-contain" />
            )}
            <a
              href={signedUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1 border-t bg-muted/30 py-1.5 text-[11px] text-muted-foreground hover:text-foreground"
            >
              Open full size <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
      </div>


      {row.status === "pending_review" && (
        <div className="relative mt-4 space-y-2">
          {rejecting ? (
            <div className="space-y-2">
              <Input
                placeholder="Reason (shown to user)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                autoFocus
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="destructive"
                  className="flex-1"
                  disabled={busy || reason.trim().length < 3}
                  onClick={() => onReject(reason.trim())}
                >
                  Confirm reject
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setRejecting(false); setReason(""); }}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1 bg-emerald-600 text-white hover:bg-emerald-500"
                disabled={busy}
                onClick={onApprove}
              >
                <Check className="me-1 h-3.5 w-3.5" /> Approve & activate
              </Button>
              <Button size="sm" variant="outline" onClick={() => setRejecting(true)} disabled={busy}>
                <X className="me-1 h-3.5 w-3.5" /> Reject
              </Button>
            </div>
          )}
        </div>
      )}

      {row.status !== "pending_review" && (
        <div
          className={`relative mt-4 rounded-lg border px-3 py-2 text-xs ${
            row.status === "approved"
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
              : "border-red-500/30 bg-red-500/10 text-red-400"
          }`}
        >
          {row.status === "approved" ? "Approved" : "Rejected"} on{" "}
          {row.reviewed_at ? new Date(row.reviewed_at).toLocaleString() : "—"}
          {row.admin_notes && <div className="mt-1 text-muted-foreground">Reason: {row.admin_notes}</div>}
        </div>
      )}
    </div>
  );
}
